import { type FormEvent, useState } from 'react';
import type { Role } from '../api/session';
import { type User, generatePassword } from '../api/users';
import { Icon } from './Icon';

const ROLE_LABELS: Record<Role, string> = { admin: 'Administrator', instructor: 'Ausbilder' };

// Shared by both password-reset forms below, so their submit/cancel pair cannot drift apart.
function FormButtons({
  submitLabel,
  busy,
  onCancel,
}: { submitLabel: string; busy: boolean; onCancel: () => void }) {
  return (
    <div className="button-group">
      <button type="submit" className="button primary" disabled={busy}>
        {submitLabel}
      </button>
      <button type="button" className="button quiet" onClick={onCancel}>
        Abbrechen
      </button>
    </div>
  );
}

// One user row with its own password-reset form. The two `on*PasswordChanged` callbacks
// resolve to whether the update actually saved; the form only closes on true, so a failed
// request leaves it open instead of quietly discarding what was typed.
export function UserRow({
  user,
  isSelf,
  busy,
  onRoleChange,
  onToggleDisabled,
  onDelete,
  onPasswordChanged,
  onOwnPasswordChanged,
}: {
  user: User;
  isSelf: boolean;
  busy: boolean;
  onRoleChange: (role: Role) => void;
  onToggleDisabled: () => void;
  onDelete: () => void;
  onPasswordChanged: (password: string) => Promise<boolean>;
  onOwnPasswordChanged: (password: string) => Promise<boolean>;
}) {
  const [resetting, setResetting] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [ownPassword1, setOwnPassword1] = useState('');
  const [ownPassword2, setOwnPassword2] = useState('');
  const [ownMismatch, setOwnMismatch] = useState(false);

  const openReset = () => {
    setResetting(true);
    setResetPassword(generatePassword());
    setOwnPassword1('');
    setOwnPassword2('');
    setOwnMismatch(false);
  };

  const submitOtherReset = async (e: FormEvent) => {
    e.preventDefault();
    if (await onPasswordChanged(resetPassword)) setResetting(false);
  };

  // The only flow that kills the acting admin's own session: ask twice, refuse a mismatch
  // before any request, warn plainly before sending.
  const submitOwnReset = async (e: FormEvent) => {
    e.preventDefault();
    if (ownPassword1 !== ownPassword2) {
      setOwnMismatch(true);
      return;
    }
    if (
      !window.confirm(
        'Das eigene Passwort zu ändern meldet dieses Konto überall ab, auch hier. Fortfahren?',
      )
    ) {
      return;
    }
    if (await onOwnPasswordChanged(ownPassword1)) setResetting(false);
  };

  return (
    <li>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <strong>{user.displayName}</strong>
          <span>
            {' '}
            · @{user.username} · {ROLE_LABELS[user.role]}
            {user.disabled ? ' · gesperrt' : ''}
          </span>
        </div>
        <label className="flex items-center gap-2 text-xs" htmlFor={`role-${user.id}`}>
          Rolle
          <select
            id={`role-${user.id}`}
            value={user.role}
            disabled={busy}
            onChange={(e) => onRoleChange(e.target.value as Role)}
          >
            <option value="instructor">Ausbilder</option>
            <option value="admin">Administrator</option>
          </select>
        </label>
      </div>
      <div className="button-group mt-3">
        <button
          type="button"
          className="button"
          onClick={() => (resetting ? setResetting(false) : openReset())}
        >
          <Icon name="edit" size={14} />
          Passwort {resetting ? 'abbrechen' : 'ändern'}
        </button>
        <button type="button" className="button quiet" disabled={busy} onClick={onToggleDisabled}>
          {user.disabled ? 'Entsperren' : 'Sperren'}
        </button>
        <button
          type="button"
          className="button quiet icon-button danger-subtle"
          aria-label={`Benutzer ${user.displayName} löschen`}
          disabled={busy}
          onClick={onDelete}
        >
          <Icon name="trash" size={15} />
        </button>
      </div>
      {resetting ? (
        isSelf ? (
          <form className="mt-3 flex max-w-sm flex-col gap-3" onSubmit={submitOwnReset}>
            <p className="notice warning">
              Das eigene Passwort ändern meldet dieses Konto überall ab, auch hier - danach folgt
              die Anmeldeseite.
            </p>
            <label className="flex flex-col gap-1 text-sm" htmlFor={`own-password-1-${user.id}`}>
              <span>Neues Passwort</span>
              <input
                id={`own-password-1-${user.id}`}
                type="password"
                autoComplete="new-password"
                minLength={12}
                maxLength={256}
                required
                value={ownPassword1}
                onChange={(e) => {
                  setOwnPassword1(e.target.value);
                  setOwnMismatch(false);
                }}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm" htmlFor={`own-password-2-${user.id}`}>
              <span>Wiederholen</span>
              <input
                id={`own-password-2-${user.id}`}
                type="password"
                autoComplete="new-password"
                minLength={12}
                maxLength={256}
                required
                value={ownPassword2}
                onChange={(e) => {
                  setOwnPassword2(e.target.value);
                  setOwnMismatch(false);
                }}
              />
            </label>
            {ownMismatch ? (
              <p className="notice warning" role="alert">
                Die Passwörter stimmen nicht überein.
              </p>
            ) : null}
            <FormButtons
              submitLabel="Ändern und abmelden"
              busy={busy}
              onCancel={() => setResetting(false)}
            />
          </form>
        ) : (
          <form className="mt-3 flex max-w-sm flex-col gap-3" onSubmit={submitOtherReset}>
            <label className="flex flex-col gap-1 text-sm" htmlFor={`reset-password-${user.id}`}>
              <span>Neues Passwort</span>
              <input
                id={`reset-password-${user.id}`}
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                minLength={12}
                maxLength={256}
                required
              />
            </label>
            <p className="muted">
              Wird nur einmal angezeigt. Setzt das Passwort und meldet alle Sitzungen dieses
              Benutzers ab.
            </p>
            <FormButtons
              submitLabel="Passwort setzen"
              busy={busy}
              onCancel={() => setResetting(false)}
            />
          </form>
        )
      ) : null}
    </li>
  );
}
